import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { AverageQuestionOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { averagesPrompts as en } from "../Files/Prompts/en/averages.ts";
import { averagesPrompts as es } from "../Files/Prompts/es/averages.ts";
import { averagesPrompts as fr } from "../Files/Prompts/fr/averages.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Mean, median, mode and range of a stated list. See ADR 0039 §2.
 *
 * Averages is the first format on this site whose input is a *list* rather
 * than one or two operands (ADR 0039 §Consequences), and the mean is the one
 * question that constrains what the list is allowed to be: a mean of 6.4 is a
 * decimals question, and Decimals is a format
 * (ADR 0032 §4, ADR 0034 §3). So the mean list is built by construction -
 * start every entry at the mean, then move value back and forth between pairs
 * of entries by an amount that keeps both inside range. Every such move
 * preserves the total, so the list sums to `mean * length` exactly, on every
 * draw, with no rejection loop and no failure path - the same preference
 * ShapeGenerator states for its own aspect-ratio bound.
 *
 * Median and range need no such build-up; median gets its guarantee for free
 * from AVERAGE_LIST_LENGTH_OPTIONS having no even entry (an odd-length sorted
 * list always has a genuine middle element). Mode is the other constructed
 * one: a list with two modes has two correct answers and a key can only print
 * one, so the repeated value is chosen deliberately and every other value is
 * drawn distinct so it cannot accidentally tie.
 */

/** Values a list entry may take. Wide enough that a mode's fillers never run out. */
export const VALUE_MIN = 1;
export const VALUE_MAX = 20;

/** The mean itself, kept off the edges of VALUE_MIN..VALUE_MAX so both directions of the perturbation below have somewhere to go. */
const MEAN_MIN = 3;
const MEAN_MAX = 17;

function questionTypes(question: AverageQuestionOptions): AverageQuestionOptions[] {
    if (question === AverageQuestionOptions.Mixed) {
        return [
            AverageQuestionOptions.Mean,
            AverageQuestionOptions.Median,
            AverageQuestionOptions.Mode,
            AverageQuestionOptions.Range,
        ];
    }
    return [question];
}

/**
 * A list of `length` values, independently drawn. Fine for median and range,
 * neither of which needs the values to relate to one another.
 */
function plainList(length: number): number[] {
    return Array.from({ length }, () => getRandomInt(VALUE_MIN, VALUE_MAX));
}

/**
 * A list of `length` values that sums to exactly `mean * length`.
 *
 * Starts every entry at the mean - already correct, if uninteresting - then
 * shuffles value between random pairs of entries. Each move adds an amount to
 * one and subtracts the same amount from the other, so the sum never changes;
 * the amount is capped so neither entry leaves VALUE_MIN..VALUE_MAX. More
 * rounds than entries so a list rarely comes out looking untouched.
 */
function meanList(length: number, mean: number): number[] {
    const list = new Array(length).fill(mean);
    const rounds = length * 3;

    for (let round = 0; round < rounds; round++) {
        const i = getRandomInt(0, length - 1);
        let j = getRandomInt(0, length - 1);
        while (j === i) {
            j = getRandomInt(0, length - 1);
        }
        const lowestMove = Math.max(VALUE_MIN - list[i], list[j] - VALUE_MAX);
        const highestMove = Math.min(VALUE_MAX - list[i], list[j] - VALUE_MIN);
        if (lowestMove > highestMove) {
            // No move keeps both entries in range this round; skip it.
            continue;
        }
        const move = getRandomInt(lowestMove, highestMove);
        list[i] += move;
        list[j] -= move;
    }

    return shuffle(list);
}

/** `count` distinct values from VALUE_MIN..VALUE_MAX, none equal to `exclude`. */
function distinctValuesExcluding(count: number, exclude: number): number[] {
    const pool: number[] = [];
    for (let value = VALUE_MIN; value <= VALUE_MAX; value++) {
        if (value !== exclude) {
            pool.push(value);
        }
    }
    return shuffle(pool).slice(0, count);
}

/**
 * A list with exactly one mode: a chosen value repeated more times than any
 * other value appears. The other entries are drawn distinct from each other
 * and from the mode, so none of them can tie it - they each appear once,
 * which is fewer than the two-or-more times the mode repeats.
 */
function modeList(length: number): { list: number[]; mode: number } {
    const mode = getRandomInt(VALUE_MIN, VALUE_MAX);
    // At least twice, so it is actually a repeat; at most half the list
    // rounded up, so it does not swamp it. Drawing up to `length` put
    // `8, 8, 8, 8, 2, 8, 8` on a generated sheet - a mode question that asks
    // nothing, because there is barely anything else in the list to compare.
    const repeats = getRandomInt(2, Math.max(2, Math.ceil(length / 2)));
    const fillers = distinctValuesExcluding(length - repeats, mode);
    const list = shuffle([...new Array(repeats).fill(mode), ...fillers]);
    return { list, mode };
}

function buildProblem(
    question: AverageQuestionOptions,
    length: number,
    language: PrintedLanguage
): { prompt: string; answer: string } {
    const t = PROMPTS[language];
    switch (question) {
        case AverageQuestionOptions.Mean: {
            const mean = getRandomInt(MEAN_MIN, MEAN_MAX);
            const list = meanList(length, mean);
            return {
                prompt: t.mean(list.join(", ")),
                answer: String(mean),
            };
        }
        case AverageQuestionOptions.Median: {
            const list = plainList(length);
            const sorted = [...list].sort((a, b) => a - b);
            const median = sorted[(sorted.length - 1) / 2];
            return {
                prompt: t.median(list.join(", ")),
                answer: String(median),
            };
        }
        case AverageQuestionOptions.Mode: {
            const { list, mode } = modeList(length);
            return {
                prompt: t.mode(list.join(", ")),
                answer: String(mode),
            };
        }
        default: {
            // Range, and Mixed's fallback - matches the pattern
            // NumberPropertyGenerator's buildProblem uses for its own default case.
            const list = plainList(length);
            const range = Math.max(...list) - Math.min(...list);
            return {
                prompt: t.range(list.join(", ")),
                answer: String(range),
            };
        }
    }
}

/**
 * One averages problem. Always succeeds.
 *
 * `display` is `[prompt, answer]`, the same shape as Number Properties and
 * Place Value - the list itself lives inside the prompt string, since there is
 * nothing else to pull out of it the way a rectangle or a number line is.
 */
export function buildAveragesProblem(
    question: AverageQuestionOptions,
    listLength: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    const built = buildProblem(question, Math.max(3, Math.floor(listLength) || 7), language);
    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        averageQuestion: question,
    };
}

/** A page-shaped set of averages problems. */
export function generateAveragesPages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.AverageQuestion ?? AverageQuestionOptions.Mixed);
    const listLength = settings.AverageListLength ?? 7;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildAveragesProblem(question, listLength, key, language),
        fingerprint: (problem) => problem.display![0],
    });
}
